import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';

// SLA Indicator Component - Visual status indicator for SLA compliance
// Shows green/yellow/red based on time remaining or breach status
//
// SLA Definitions:
// - Standard: Within 3 business days
// - Priority: Next business day
// - Urgent: Same day (end of business)
// - On-Demand: Customer-specified deadline

type SlaStatus = 'breached' | 'at-risk' | 'warning' | 'on-track' | 'none';
type SlaSize = 'xs' | 'sm' | 'md' | 'lg';
type SlaPriority = 'urgent' | 'on-demand' | 'priority' | 'standard';

interface StatusConfig {
  color: string;
  bgColor: string;
  label: string;
  icon: string;
}

interface SlaIndicatorProps {
  dueAt?: string | Date | null;
  isAtRisk?: boolean;
  isBreached?: boolean;
  priority?: string;
  size?: SlaSize;
  showLabel?: boolean;
  brand?: BrandConfig;
}

const SlaIndicator: React.FC<SlaIndicatorProps> = ({
  dueAt,
  isAtRisk,
  isBreached,
  priority = 'standard',
  size = 'sm',
  showLabel = false,
  brand
}) => {
  // Calculate status based on time remaining
  const getStatus = (): SlaStatus => {
    if (isBreached) return 'breached';
    if (isAtRisk) return 'at-risk';
    if (!dueAt) return 'none';

    const now = Date.now(); // eslint-disable-line react-hooks/purity -- SLA status must reflect current time at render
    const due = new Date(dueAt).getTime();
    const hoursRemaining = (due - now) / (1000 * 60 * 60);

    if (hoursRemaining < 0) return 'breached';

    // Thresholds based on priority urgency
    // Urgent/On-Demand: tighter thresholds since deadlines are same-day or specific
    // Priority: medium thresholds for next-day items
    // Standard: relaxed thresholds for 3-day items
    const thresholds: Record<SlaPriority, { atRisk: number; warning: number }> = {
      urgent: { atRisk: 1, warning: 2 },       // At risk < 1h, warning < 2h
      'on-demand': { atRisk: 1, warning: 2 },  // At risk < 1h, warning < 2h
      priority: { atRisk: 2, warning: 4 },     // At risk < 2h, warning < 4h
      standard: { atRisk: 4, warning: 8 },     // At risk < 4h, warning < 8h
    };

    const { atRisk: atRiskThreshold, warning: warningThreshold } =
      thresholds[priority as SlaPriority] || thresholds.standard;

    if (hoursRemaining < atRiskThreshold) return 'at-risk';
    if (hoursRemaining < warningThreshold) return 'warning';
    return 'on-track';
  };

  const status = getStatus();

  const statusConfig: Record<SlaStatus, StatusConfig> = {
    breached: {
      color: brand?.colors?.error || '#EF4444',
      bgColor: '#FEE2E2',
      label: 'Breached',
      icon: 'X',
    },
    'at-risk': {
      color: brand?.colors?.warning || '#F59E0B',
      bgColor: '#FEF3C7',
      label: 'At Risk',
      icon: '!',
    },
    warning: {
      color: '#D97706',
      bgColor: '#FEF3C7',
      label: 'Due Soon',
      icon: '!',
    },
    'on-track': {
      color: brand?.colors?.success || '#10B981',
      bgColor: '#D1FAE5',
      label: 'On Track',
      icon: '\u2713',
    },
    none: {
      color: brand?.colors?.textLight || '#6B7280',
      bgColor: '#F3F4F6',
      label: 'No SLA',
      icon: '-',
    },
  };

  const config = statusConfig[status];

  const sizeClasses: Record<SlaSize, string> = {
    xs: 'w-2 h-2',
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const labelSizeClasses: Record<SlaSize, string> = {
    xs: 'text-xs',
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-sm',
  };

  if (showLabel) {
    return (
      <div className="flex items-center gap-1.5">
        <span
          className={`inline-flex items-center justify-center rounded-full ${sizeClasses[size]}`}
          style={{ backgroundColor: config.color }}
          aria-label={config.label}
        />
        <span
          className={`font-medium ${labelSizeClasses[size]}`}
          style={{ color: config.color }}
        >
          {config.label}
        </span>
      </div>
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center rounded-full ${sizeClasses[size]}`}
      style={{ backgroundColor: config.color }}
      title={config.label}
      aria-label={config.label}
    />
  );
};

export default SlaIndicator;
